import {
  CustomerType,
  SubscriptionSource,
  SubscriptionStatus,
  SubscriptionUser,
  UserSubscriptionState,
} from "app/models/SubscriptionPlanModel";

const matchesEnum = (value: string, enumValue: string): boolean => {
  return value?.toLowerCase() === enumValue.toLowerCase();
};

/**
 * derives the subscription flags the page branches on: how the plan is paid
 * for, whether it is a licensed seat someone else pays for, and its state
 */
export const getUserSubscriptionState = (
  user: SubscriptionUser,
): UserSubscriptionState => {
  if (!user) {
    return {
      hasActiveStripeSubscription: false,
      hasStripeAccountType: false,
      hasLicensedSubscription: false,
      hasActiveTrialOrSubscription: false,
      userHasCanceled: false,
    };
  }

  const hasActiveStripeSubscription = matchesEnum(
    user?.plan?.source,
    SubscriptionSource.STRIPE,
  );

  const hasStripeAccountType = matchesEnum(
    user?.customerType,
    CustomerType.STRIPE,
  );

  const hasLicensedSubscription = Boolean(user.plan?.licensed);

  const isTrialStatus = matchesEnum(
    user?.plan?.status,
    SubscriptionStatus.TRIAL,
  );

  const isActiveStatus = matchesEnum(
    user?.plan?.status,
    SubscriptionStatus.ACTIVE,
  );

  const hasActiveTrialOrSubscription =
    isTrialStatus || isActiveStatus || Boolean(user?.hasActiveSubscription);

  const userHasCanceled = Boolean(user?.plan?.canceledAt);

  return {
    hasActiveStripeSubscription,
    hasStripeAccountType,
    hasLicensedSubscription,
    hasActiveTrialOrSubscription,
    userHasCanceled,
  };
};

/**
 * determines if a user has an active Pro trial subscription
 *
 * a Pro trial is active when all of the following conditions are met:
 * the subscription source is Stripe
 * the customer account type is Stripe
 * the subscription is NOT a licensed subscription
 * the user has an active trial or subscription
 * the user has NOT canceled their subscription
 *
 */
export const hasActiveProTrial = (user: SubscriptionUser): boolean => {
  if (!user) {
    return false;
  }

  const state = getUserSubscriptionState(user);

  return (
    state.hasActiveStripeSubscription &&
    state.hasStripeAccountType &&
    !state.hasLicensedSubscription &&
    state.hasActiveTrialOrSubscription &&
    !state.userHasCanceled
  );
};

/**
 * determines if a user is a Stripe customer with an active subscription
 *
 * a stripe user is identified when all of the following conditions are met:
 * the subscription source is Stripe
 * the customer account type is Stripe
 * the subscription is NOT a licensed/enterprise subscription
 * the user has an active trial OR active subscription
 * the user has NOT canceled their subscription
 *
 * note: this is similar to hasActiveProTrial but checks for either trial OR active status
 *
 */
export const isStripeUser = (user: SubscriptionUser): boolean => {
  if (!user) {
    return false;
  }

  const state = getUserSubscriptionState(user);

  return (
    state.hasActiveStripeSubscription &&
    state.hasStripeAccountType &&
    !state.hasLicensedSubscription &&
    state.hasActiveTrialOrSubscription &&
    !state.userHasCanceled
  );
};

/**
 * checks if a user has a canceled trial subscription
 */
export const hasCanceledTrial = (user: SubscriptionUser): boolean => {
  if (!user) {
    return false;
  }

  const isTrial = matchesEnum(user?.plan?.status, SubscriptionStatus.TRIAL);
  const hasCanceled = Boolean(user?.plan?.canceledAt);

  return isTrial && hasCanceled;
};

/**
 * checks if a user's subscription is managed by stripe
 */
export const isStripeSubscription = (user: SubscriptionUser): boolean => {
  return matchesEnum(user?.plan?.source, SubscriptionSource.STRIPE);
};
